import { useQuery } from "@apollo/client";
import { useEffect } from "react";
import { batch } from "react-redux";
import { setQrCode } from "slices/accessSlice";
import { setInitialDishTypes } from "slices/dishesSlice";
import { setDoorLockState } from "slices/doorSlice";
import { setFridgeId, setFridgeState, setSegments } from "slices/fridgeInfoSlice";
import { setLightingMode } from "slices/fridgeLightingSlice";
import { selectOrder, setOrder, setRefill } from "slices/transactionSlice";
import { AppDispatch } from "store";
import { setFridgeNameToLocalStorage, setupNewFridgeId } from "utils/browser/localStorage";
import { INITIAL_STATE_QUERY } from "utils/gqlOperations";
import { logToSentry } from "utils/logger";
import { deepOmit } from "utils/utils";

import { useAppDispatch, useAppSelector } from "hooks/utilsHooks/storeHooks";
import { Fridge, Order } from "types";
import { LocalFridgeState } from "types/mainTypes";

import { setInitialDataLoaded } from "../../slices/appStateSlice";

export const handleSetInitialData = (
	data: Fridge | LocalFridgeState,
	appDispatch: AppDispatch,
	order?: Order | null,
) => {
	const cleanedData = deepOmit(data, ["__typename"]) as LocalFridgeState;
	setFridgeNameToLocalStorage(cleanedData.name);
	batch(() => {
		appDispatch(setFridgeId(cleanedData.id));
		appDispatch(setFridgeState(cleanedData.state ?? null));
		if (cleanedData.access?.qrCode?.code) {
			appDispatch(setQrCode(cleanedData.access.qrCode.code));
		}
		if (cleanedData.dishes?.types) {
			appDispatch(setInitialDishTypes(cleanedData.dishes?.types));
		}
		if (cleanedData.segments) {
			appDispatch(setSegments(cleanedData.segments));
		}
		if (cleanedData.door) {
			appDispatch(setDoorLockState(cleanedData.door));
		}
		if (cleanedData.lighting?.mode) {
			appDispatch(setLightingMode(cleanedData.lighting.mode));
		}
		if (cleanedData.transaction?.orders?.last) {
			appDispatch(setOrder(cleanedData.transaction.orders.last));
		}
		if (cleanedData.transaction?.refills?.last) {
			logToSentry(
				`Initial state has active refill on fridge: ${cleanedData.id}, mode: ${cleanedData.transaction.refills.last.mode}`,
				"info",
			);
			appDispatch(setRefill(cleanedData.transaction.refills.last));
		}
		if (order && order.state && !cleanedData.transaction) {
			appDispatch(setOrder(null));
		}
	});
	appDispatch(setInitialDataLoaded(true));
};

type Props = {
	fridgeId: string;
};

export const useInitialStateQuery = ({ fridgeId }: Props) => {
	const appDispatch = useAppDispatch();
	const order = useAppSelector(selectOrder);

	const { data, loading, refetch } = useQuery(INITIAL_STATE_QUERY, {
		variables: { id: fridgeId },
		fetchPolicy: "network-only",
		refetchWritePolicy: "overwrite",
		onCompleted: ({ fridge }) => {
			handleSetInitialData(fridge, appDispatch, order);
		},
	});

	useEffect(() => {
		if (data?.fridge?.error && data.fridge.error.type === "UNAUTHORIZED") {
			// Prompt user to provide a valid UUID
			setupNewFridgeId(fridgeId, "Unauthorized access. Please provide a valid UUID");
		}
	}, [data?.fridge?.error, fridgeId]);

	return {
		loading,
		refetch,
		data,
	};
};
